import { Injectable } from '@angular/core';
import { FormMetadata, FORM_REGISTRY } from '../models/form-registry';
import { User } from '../models/User.interface';

@Injectable({
  providedIn: 'root'
})
export class FormModelService {

  constructor() { }

  /**
   * Gets all form metadata from the registry
   * @returns Array of all form metadata
   */
  public getAllFormMetadata(): FormMetadata[] {
    return Object.values(FORM_REGISTRY);
  }

  /**
   * Gets metadata for a specific form by form type
   * @param formType The type identifier of the form
   * @returns FormMetadata if found, undefined otherwise
   */
  public getFormMetadata(formType: string): FormMetadata | undefined {
    return FORM_REGISTRY[formType];
  }

  /**
   * Checks if a user has access to a specific form based on access control rules
   * @param formType The type identifier of the form
   * @param user The user to check access for (null if not logged in)
   * @returns true if user can access the form, false otherwise
   */
  public userCanAccessForm(formType: string, user: User | null): boolean {
    // No user means no access to any form
    if (!user) return false;
    
    const metadata = this.getFormMetadata(formType);
    
    // Form not found means no access
    if (!metadata) return false;
    
    // No access control means accessible to all authenticated users
    if (!metadata.accessControl) return true;
    
    const { requiresPfizerAccess, requiresAdmin } = metadata.accessControl;
    
    // Check admin requirement
    if (requiresAdmin && !user.isAdmin) {
      return false;
    }
    
    // Check Pfizer access requirement
    if (requiresPfizerAccess && !user.hasPfizerFormAccess) {
      return false;
    }
    
    // All checks passed
    return true;
  }

  /**
   * Gets all forms that the user has permission to access
   * @param user The user to check access for (null if not logged in)
   * @returns Array of FormMetadata for accessible forms
   */
  public getAccessibleForms(user: User | null): FormMetadata[] {
    // No user means no accessible forms
    if (!user) return [];
    
    return this.getAllFormMetadata().filter(form => 
      this.userCanAccessForm(form.formType, user)
    );
  }

  /**
   * Gets the access denied message for a specific form
   * @param formType The type identifier of the form
   * @returns Custom message if defined, default message otherwise
   */
  public getAccessDeniedMessage(formType: string): string {
    const metadata = this.getFormMetadata(formType);
    
    // If form has a custom access denied message, use it
    if (metadata?.accessControl?.accessDeniedMessage) {
      return metadata.accessControl.accessDeniedMessage;
    }
    
    // Build a default message based on requirements
    if (metadata?.accessControl) {
      const { requiresAdmin, requiresPfizerAccess } = metadata.accessControl;
      
      if (requiresAdmin && requiresPfizerAccess) {
        return 'This form requires administrator privileges and Pfizer form access.';
      } else if (requiresAdmin) {
        return 'This form is only accessible to administrators.';
      } else if (requiresPfizerAccess) {
        return 'This form requires Pfizer form access. Please contact your administrator.';
      }
    }
    
    // Generic fallback message
    return 'You do not have permission to access this form.';
  }

  /**
   * Checks if a form has any access restrictions
   * @param formType The type identifier of the form
   * @returns true if form has access restrictions, false otherwise
   */
  public formHasAccessRestrictions(formType: string): boolean {
    const metadata = this.getFormMetadata(formType);
    return !!metadata?.accessControl;
  }

  /**
   * Gets a summary of access requirements for a form
   * @param formType The type identifier of the form
   * @returns Object describing access requirements
   */
  public getFormAccessRequirements(formType: string): {
    hasRestrictions: boolean;
    requiresAdmin: boolean;
    requiresPfizerAccess: boolean;
  } {
    const metadata = this.getFormMetadata(formType);
    const accessControl = metadata?.accessControl;
    
    return {
      hasRestrictions: !!accessControl,
      requiresAdmin: accessControl?.requiresAdmin ?? false,
      requiresPfizerAccess: accessControl?.requiresPfizerAccess ?? false,
    };
  }
}